import { AreaConfig, CategoryId } from '#server/models';
import { useAdminSubjectDraft } from '@/contexts';
import { toggleCategoryId, toggleId } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';

interface Step4Props {
  areaConfig: AreaConfig;
}

export const Step4Categories = ({ areaConfig }: Step4Props) => {
  const { draft, updateDraft } = useAdminSubjectDraft();

  const toggleCategory = (catId: CategoryId) => {
    updateDraft({ categoryIds: toggleCategoryId(draft.categoryIds, catId) });
  };

  const toggleUnit = (unitId: number) => {
    updateDraft({ unitIds: toggleId(draft.unitIds, unitId) });
  };

  return (
    <div className="space-y-8">
      <div className="space-y-5">
        <h2 className="text-base font-semibold text-[#E8E8E8]">Step 4 – Categories &amp; Units</h2>
        <p className="text-sm text-[#999]">Select the categories and units that apply to this {areaConfig.subjectName.singular.toLowerCase()}.</p>
      </div>

      <section className="space-y-4">
        <h3 className="text-sm font-semibold text-[#C3D82E] uppercase tracking-wide">Categories</h3>
        <div className="space-y-4">
          {areaConfig.categoryGroups.map((group) => (
            <div key={group.id} className="space-y-2">
              <h4 className="text-sm font-medium text-[#CCC]">
                {group.name}
                {group.isPrimary && <span className="ml-2 text-xs text-[#888] font-normal">(primary)</span>}
              </h4>
              <div className="flex flex-wrap gap-3">
                {group.categories.map((cat) => {
                  const catId: CategoryId = { id: cat.id, groupId: group.id };
                  const checked = draft.categoryIds.some((c) => c.id === cat.id && c.groupId === group.id);
                  return (
                    <label key={cat.id} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox checked={checked} onChange={() => toggleCategory(catId)} />
                      <span className="flex items-center gap-1.5 text-sm text-[#CCC]">
                        {cat.color && (
                          <span
                            className="inline-block w-3 h-3 rounded-full border border-[#555]"
                            style={{ backgroundColor: cat.color }}
                          />
                        )}
                        {cat.name}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        {draft.categoryIds.length > 0 && (
          <p className="text-xs text-[#666]">
            {draft.categoryIds.length} categor{draft.categoryIds.length === 1 ? 'y' : 'ies'} selected
          </p>
        )}
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-[#C3D82E] uppercase tracking-wide">Units</h3>
        <div className="space-y-2">
          {areaConfig.units.map((unit) => (
            <label key={unit.id} className="flex items-center gap-3 cursor-pointer group">
              <Checkbox
                checked={draft.unitIds.includes(unit.id)}
                onChange={() => toggleUnit(unit.id)}
              />
              <span className="font-mono text-xs text-[#C3D82E] w-28 shrink-0">{unit.udc}</span>
              <span className="text-sm text-[#CCC] group-hover:text-[#E8E8E8] transition-colors">{unit.name}</span>
            </label>
          ))}
        </div>
        {draft.unitIds.length > 0 && (
          <p className="text-xs text-[#666]">
            {draft.unitIds.length} unit{draft.unitIds.length === 1 ? '' : 's'} selected
          </p>
        )}
      </section>
    </div>
  );
};
